import { JourneyZone, ZoneActionConfig, JourneyActionOverride } from '../models/checkout-journey.types';

export function combineAvailabilityAndJourneyConfigs<T>(
  zone: JourneyZone,
  availabilityConfig: T | undefined,
  journeyConfig: T | undefined,
): T | undefined {
  if (!availabilityConfig && !journeyConfig) return undefined;
  if (!journeyConfig) return availabilityConfig;
  if (!availabilityConfig) return journeyConfig;

  if (zone === 'actions') {
    return mergeZoneActions(availabilityConfig as ZoneActionConfig[], journeyConfig as JourneyActionOverride[]) as T;
  }
  if (zone === 'summaryContent') {
    return mergeSummaryContent(availabilityConfig, journeyConfig) as T;
  }
  return journeyConfig;
}

export function applyPageOverrides<T>(
  zone: JourneyZone,
  baseConfig: T | undefined,
  pageOverrides?: JourneyActionOverride[],
): T | undefined {
  if (zone !== 'actions' || !pageOverrides?.length || !baseConfig) return baseConfig;
  return mergeZoneActions(baseConfig as ZoneActionConfig[], pageOverrides) as T;
}

export function mergeSummaryContent<T>(
  availabilityConfig: T | undefined,
  journeyOverride: Partial<T> | undefined,
): T | undefined {
  if (!availabilityConfig) return journeyOverride as T | undefined;
  if (!journeyOverride) return availabilityConfig;
  return { ...availabilityConfig, ...journeyOverride };
}

export function mergeZoneActions(
  baseActions: ZoneActionConfig[] | undefined,
  overrides: JourneyActionOverride[] | undefined,
): ZoneActionConfig[] {
  if (!baseActions) return [];
  if (!overrides?.length) return baseActions;

  const overridesById = new Map<string, JourneyActionOverride>();
  overrides.forEach((o) => overridesById.set(o.id, o));

  const merged = baseActions.map((action) =>
    action.id && overridesById.has(action.id)
      ? ({ ...action, ...overridesById.get(action.id) } as ZoneActionConfig)
      : action,
  );

  const unmatched = overrides.filter((o) => !baseActions.some((a) => a.id === o.id));
  return [...merged, ...(unmatched as ZoneActionConfig[])];
}
